/**
 * Luxury tasks: a peon spends gold to post a task that pays extra to
 * whoever finishes it. See docs/economy.md section 3.
 *
 * Pure and dependency-free, like shared/bounty.ts and shared/rewards.ts, so
 * it runs on the server, in the browser and in the Node test runner.
 */
export const LUXURY_TIERS = [100, 500, 1000] as const;

export type LuxuryTier = (typeof LUXURY_TIERS)[number];

/** Fraction of the tier paid to whoever finishes a luxury task. */
export const LUXURY_PAYOUT = 0.5;

/** Gold paid to whoever finishes a luxury task bought at `tier`. */
export function luxuryPayout(tier: LuxuryTier): number {
  return Math.round(tier * LUXURY_PAYOUT);
}

export function isLuxuryTier(n: number): n is LuxuryTier {
  return (LUXURY_TIERS as readonly number[]).includes(n);
}

// ---------- The luxury auction (docs/bounties.md "Auction", flag auctionUI for the display only) ----------

/** A luxury task nobody has finished this long after it was posted starts to rise. */
export const AUCTION_GRACE_MS = 48 * 3_600_000;
/** Each day from the grace's end adds this fraction of the offered payout. */
export const AUCTION_STEP = 0.1;
/** The total never passes this many times the offered payout. */
export const AUCTION_CAP = 2.0;
const AUCTION_DAY_MS = 24 * 3_600_000;

/**
 * Rising days at `at` for a luxury task posted at `createdAt` (ISO): 0 within the first 48 hours,
 * 1 from the 48th hour (the first further day), 2 from the 72nd, and so on, never past the cap's
 * days. Zero for an unreadable date.
 */
export function auctionDays(createdAt: string, at: number): number {
  const since = Date.parse(createdAt);
  if (!Number.isFinite(since)) return 0;
  const over = at - since - AUCTION_GRACE_MS;
  if (over < 0) return 0;
  const maxDays = Math.round((AUCTION_CAP - 1) / AUCTION_STEP);
  return Math.min(maxDays, Math.floor(over / AUCTION_DAY_MS) + 1);
}

/**
 * The house's premium on a luxury task finished at `doneAt` (ms): `offered` (the payout the task
 * offers, luxuryPayout of its tier) times AUCTION_STEP per rising day, capped so the total stays at
 * most AUCTION_CAP times the offer. Whole gold; 0 within the grace. The offerer is never charged it:
 * the house pays it as a separate bounty row (server/db.ts completeTask).
 */
export function auctionPremium(offered: number, createdAt: string, doneAt: number): number {
  if (!(offered > 0)) return 0;
  const days = auctionDays(createdAt, doneAt);
  return Math.min(Math.round(offered * AUCTION_STEP * days), Math.round(offered * (AUCTION_CAP - 1)));
}

export interface AuctionPrice {
  /** What the offerer's tier pays the finisher (luxuryPayout). */
  offered: number;
  /** What the house adds on top; 0 while the task is not rising. */
  premium: number;
  /** offered + premium: what finishing it now pays. */
  total: number;
  /** Rising days (auctionDays). */
  days: number;
}

/**
 * What an open luxury task pays if finished at `now`, split into the offer and the house's part;
 * null for an ordinary task or a done one. Shared by both clients' cards and task screens.
 */
export function auctionPriceNow(task: { luxury: number; created_at: string; status?: string }, now: number): AuctionPrice | null {
  if (!isLuxuryTier(task.luxury) || (task.status !== undefined && task.status !== "open")) return null;
  const offered = luxuryPayout(task.luxury);
  const premium = auctionPremium(offered, task.created_at, now);
  return { offered, premium, total: offered + premium, days: auctionDays(task.created_at, now) };
}

// A rising luxury task has no line of its own on the task screen: the meta line's total is the whole
// story (owner, 2026-10-05, of "The house adds 25 gold because nobody has taken it yet.": "This entire
// description shouldn't be here").

/** The push sent once when a luxury task first starts rising, to everyone but its offerer. */
export function auctionPushCopy(title: string): { title: string; body: string } {
  return { title: `Rising: ${title}`, body: "Nobody has taken it yet." };
}

// ---------- Luxury expiry (owner, 2026-10-24) ----------
// "Luxury tasks should last until they reach full value plus one day. At that point the poster is
// refunded, notified, and it's removed." Full value is the auction's cap (AUCTION_CAP times the
// offer), reached on the last rising day; the task lasts one more day. Applied whatever auctionUI
// says: data never depends on a UI flag. Run from the sweep (server/auction.ts expireLuxuryTasks).

/** How long a luxury task lasts once it has reached full value. */
export const LUXURY_EXPIRY_AFTER_FULL_MS = AUCTION_DAY_MS;

/**
 * When a luxury task posted at `createdAt` (ISO) reaches full value (ms): the start of its last
 * rising day, AUCTION_GRACE_MS plus one day per further step (auctionDays hits its cap there).
 * NaN for an unreadable date.
 */
export function luxuryFullValueAt(createdAt: string): number {
  const maxDays = Math.round((AUCTION_CAP - 1) / AUCTION_STEP);
  return Date.parse(createdAt) + AUCTION_GRACE_MS + (maxDays - 1) * AUCTION_DAY_MS;
}

/** When a luxury task posted at `createdAt` expires (ms): full value plus one day. NaN for an unreadable date. */
export function luxuryExpiresAt(createdAt: string): number {
  return luxuryFullValueAt(createdAt) + LUXURY_EXPIRY_AFTER_FULL_MS;
}

/** Whether an open luxury task has run out at `now` (ms). False for ordinary, done and undated tasks. */
export function luxuryExpired(task: { luxury: number; created_at: string; status?: string }, now: number): boolean {
  if (!(task.luxury > 0) || (task.status !== undefined && task.status !== "open")) return false;
  const at = luxuryExpiresAt(task.created_at);
  return Number.isFinite(at) && now >= at;
}

/**
 * The push to a luxury task's poster when it runs out untaken and their gold comes back. No numbers,
 * like every push that is not an offer with a clock (docs/product.md): the purse shows the amount.
 */
export function luxuryExpiredPushCopy(title: string): { title: string; body: string } {
  return { title: `Nobody took ${title}`, body: "Your gold is back in your purse." };
}
